import React, { useContext, useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  Paper,
  TextField,
  Typography,
  useMediaQuery,
  InputAdornment,
  MenuItem,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation, Trans } from 'react-i18next';
import { Link, RouteComponentProps, useNavigate } from '@reach/router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import AppBackLink from '../../../components/BackLink';
import AppButton from '../../../components/AppButton';
import { ReactComponent as UncheckedIcon } from '../../../images/svg/checkbox-unchecked.svg';
import { ReactComponent as CheckedIcon } from '../../../images/svg/checkbox-checked.svg';
import AuthRegisterData, { schema } from '../../interfaces/register-data';
import ApiContext from '../../../api/context';
import { config } from '../../../config';
import EmailTaken from '../../errors/email-taken';
import classes from './Register.module.css';
import { toastr } from 'react-redux-toastr';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import { Helmet } from 'react-helmet';
import { getPage } from '../../../utils/pages';
import { COUNTRIES } from '../../../constants/countries';

const useStylesRegister = makeStyles((theme) => ({
  card: {
    backgroundColor: '#F9FAFB',
    border: '1px solid rgba(51, 51, 51, 0.25)',
    padding: theme.spacing(3),
  },
}));

/**
 * Sign-Up component.
 */
export default function AuthRegister(props: RouteComponentProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const api = useContext(ApiContext);
  const cardStyles = useStylesRegister();
  const isTablet = useMediaQuery(({ breakpoints }) => breakpoints.only('md'));
  const [loading, setLoading] = useState(false);
  const [globalError, setGlobalError] = useState<string | null>(null);
  const [passwordVisibility, setPasswordVisibility] = useState(false);
  const [passwordRepeatVisibility, setPasswordRepeatVisibility] = useState(false);
  
  // Phone number state management
  const [countryCode, setCountryCode] = useState(COUNTRIES[0].dial);
  const [localPhone, setLocalPhone] = useState('');
  const [phoneDirty, setPhoneDirty] = useState(false);
  const submitSeenRef = useRef(false);

  const selectedCountry = useMemo(() => COUNTRIES.find(c => c.dial === countryCode) || COUNTRIES[0], [countryCode]);

  const sanitizeLocal = (val: string) => val.replace(/[^0-9\s]/g, '');
  const composedPhone = useMemo(() => {
    const trimmed = localPhone.trim();
    return trimmed ? `${countryCode}${trimmed.replace(/\s+/g, '')}` : '';
  }, [countryCode, localPhone]);
  const defaultValues: AuthRegisterData = {
    contact: {
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
    },
    password: {
      password: '',
      confirm: '',
    },
    flags: {
      joinClub: false,
      acceptTerms: false,
    },
  };
  const { errors, getValues, watch, handleSubmit, register, setError, setValue } = useForm({
    defaultValues,
    resolver: zodResolver(schema),
  });

  // Phone number effects
  useEffect(() => {
    if (!phoneDirty && errors.contact?.phoneNumber) submitSeenRef.current = true;
  }, [errors.contact?.phoneNumber, phoneDirty]);

  useEffect(() => {
    setValue && setValue('contact.phoneNumber', composedPhone, { shouldValidate: phoneDirty, shouldDirty: phoneDirty });
  }, [composedPhone, phoneDirty, setValue]);

  const showPhoneError = (phoneDirty || submitSeenRef.current) && !!errors.contact?.phoneNumber;

  async function triggerSubmit() {
    setGlobalError(null);
    setLoading(true);
    try {
      const {
        contact: { email },
        flags: { joinClub },
      } = getValues();
      if (joinClub) {
        await api.club.invite(email, 'regular', config.tenantId);
      }
      const error = await api.auth.register(getValues());

      if (error) {
        setGlobalError(t(error));
        return;
      }
      await navigate('/');
      toastr.info(t('Toast - Account created'), t('Toast - Verify your email'));
    } catch (err) {
      console.log(err.message);
      if (err.message === 'Email address already taken.') {
        setError('contact.email', { message: t('Email address already taken') });
      } else if (err.message === 'This phone number is already in use!') {
        setError('contact.phoneNumber', { message: t('Phone number already taken') });
      } else {
        setGlobalError(t('Unexpected error occurred'));
        if (process.env.NODE_ENV === 'development') {
          console.error(err);
        }
      }
    } finally {
      setLoading(false);
    }
  }

  const handlePasswordInputType = () => {
    setPasswordVisibility(!passwordVisibility);
  };

  const handlePasswordRepeatInputType = () => {
    setPasswordRepeatVisibility(!passwordRepeatVisibility);
  };

  return (
    <Grid container spacing={4}>
      <Helmet>
        <title>{t('register-title')}</title>
        <meta name="description" content={t('register-description')} />
      </Helmet>
      <Grid item xs={12} lg={6} style={{ maxWidth: '38rem' }}>
        <Box pl={isTablet ? 5 : 0} pb={1}>
          <AppBackLink isGoBackActivated topSpacing />
        </Box>
        <div className={classes.registerSection}>
          <Paper className={cardStyles.card} elevation={0}>
            <Typography variant="h2" component="h1">
              {t('auth_register_heading', 'Register')}
            </Typography>
            <Box mb="1.5rem" />
            <form onSubmit={handleSubmit(triggerSubmit)}>
            <Box component="fieldset" mb="1.25rem">
              <TextField
                className={classes.textField}
                error={!!errors.contact?.firstName}
                fullWidth
                helperText={t(errors.contact?.firstName?.message!)}
                inputRef={register}
                name="contact.firstName"
                placeholder={`${t('Name')}*`}
                variant="outlined"
                autoComplete="given-name"
              />
              <TextField
                className={classes.textField}
                error={!!errors.contact?.lastName}
                fullWidth
                helperText={t(errors.contact?.lastName?.message!)}
                inputRef={register}
                name="contact.lastName"
                placeholder={`${t('Last Name')}*`}
                variant="outlined"
                autoComplete="family-name"
              />
              <TextField
                className={classes.textField}
                error={!!errors.contact?.email}
                fullWidth
                helperText={t(errors.contact?.email?.message!)}
                inputRef={register}
                name="contact.email"
                placeholder={`${t('Email')}*`}
                variant="outlined"
                autoComplete="email"
              />
              <div className={classes.phoneRow}>
                <TextField
                  select
                  variant="outlined"
                  value={countryCode}
                  onChange={(e) => setCountryCode(e.target.value)}
                  className={classes.countrySelect}
                  SelectProps={{
                    displayEmpty: false,
                    MenuProps: { 
                      getContentAnchorEl: null,
                      anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left',
                      },
                      transformOrigin: {
                        vertical: 'top',
                        horizontal: 'left',
                      },
                      PaperProps: {
                        className: 'country-dropdown-paper'
                      }
                    },
                    renderValue: () => (
                      <span className={classes.flagValue}>
                        <img className={classes.flagImg} src={`https://flagcdn.com/w20/${selectedCountry.iso}.png`} alt={selectedCountry.iso.toUpperCase()} />
                        {selectedCountry.dial}
                      </span>
                    )
                  }}
                >
                  {COUNTRIES.map((c) => (
                    <MenuItem key={c.dial} value={c.dial}>
                      <span className={classes.menuItemRow}>
                        <span className={classes.flagGroup}>
                          <img className={classes.flagImg} src={`https://flagcdn.com/w20/${c.iso}.png`} alt={c.iso.toUpperCase()} />
                          <span>{c.name}</span>
                        </span>
                        <span>{c.dial}</span>
                      </span>
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  className={classes.localPhone}
                  variant="outlined"
                  value={localPhone}
                  onChange={(e) => {
                    setLocalPhone(sanitizeLocal(e.target.value));
                    // Don't validate immediately - only on blur
                  }}
                  onBlur={() => {
                    setPhoneDirty(true); // Validate when user leaves field
                  }}
                  placeholder={`${t('Phone Number')}*`}
                  error={showPhoneError}
                  helperText={t(errors.contact?.phoneNumber?.message || '')}
                  fullWidth
                  inputProps={{ inputMode: 'tel' }}
                />
              </div>
              {/* Hidden field for form submission */}
              <input type="hidden" name="contact.phoneNumber" ref={register} />
            </Box>
            <Typography variant="h3" component="h2">
              {t('Password')}
            </Typography>
            <Box mb="1rem" />
            <Box component="fieldset" mb="1.5rem">
              <TextField
                className={classes.textField}
                error={!!errors.password?.password}
                fullWidth
                helperText={t(errors.password?.password?.message!)}
                inputRef={register}
                name="password.password"
                placeholder={`${t('Password')}*`}
                type={passwordVisibility ? 'text' : 'password'}
                variant="outlined"
                autoComplete="new-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" classes={{ root: classes.inputBtnWrapper }}>
                      {!passwordVisibility && (
                        <Button variant="contained" color="primary" classes={{ root: classes.inputBtn }} onClick={handlePasswordInputType}>
                          <VisibilityOffIcon />
                        </Button>
                      )}
                      {passwordVisibility && (
                        <Button variant="contained" color="primary" classes={{ root: classes.inputBtn }} onClick={handlePasswordInputType}>
                          <VisibilityIcon />
                        </Button>
                      )}
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                className={classes.textField}
                error={!!errors.password?.confirm}
                fullWidth
                helperText={t(errors.password?.confirm?.message!)}
                inputRef={register}
                name="password.confirm"
                placeholder={`${t('Confirm Password')}*`}
                type={passwordRepeatVisibility ? 'text' : 'password'}
                variant="outlined"
                autoComplete="current-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" classes={{ root: classes.inputBtnWrapper }}>
                      {!passwordRepeatVisibility && (
                        <Button variant="contained" color="primary" classes={{ root: classes.inputBtn }} onClick={handlePasswordRepeatInputType}>
                          <VisibilityOffIcon />
                        </Button>
                      )}
                      {passwordRepeatVisibility && (
                        <Button variant="contained" color="primary" classes={{ root: classes.inputBtn }} onClick={handlePasswordRepeatInputType}>
                          <VisibilityIcon />
                        </Button>
                      )}
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Box pl=".5rem" mb="1rem">
              <FormControlLabel
                control={<Checkbox checkedIcon={<CheckedIcon />} defaultChecked={defaultValues.flags.joinClub} icon={<UncheckedIcon />} />}
                inputRef={register}
                label={
                  <Box pl=".5rem">
                    <Typography style={{ fontWeight: 600 }} variant="body2">
                      {t('auth_register_join_club_label', 'Join the SuperRebel Club')}{' '}
                      <span style={{ fontWeight: 400 }}>({t('auth_register_join_club_sublabel', 'Subscribe to our Newsletter')})¹</span>
                    </Typography>
                  </Box>
                }
                name="flags.joinClub"
              />
            </Box>
            <Box pl=".5rem" mb="1rem">
              <FormControl>
                <FormControlLabel
                  control={<Checkbox checkedIcon={<CheckedIcon />} icon={<UncheckedIcon />} />}
                  inputRef={register}
                  label={
                    <Box pl=".5rem">
                      <Typography style={{ fontWeight: 600 }} variant="body2">
                        <Trans
                          i18nKey="auth_register_accept_terms_label"
                          defaultValue="Accept the Terms and Conditions"
                          t={t}
                          components={[<Link className="base-link" to={getPage('privacyPolicy')} />, <Link className="base-link" to={getPage('terms')} />]}
                        />
                      </Typography>
                    </Box>
                  }
                  name="flags.acceptTerms"
                />
                <Box hidden={!errors.flags?.acceptTerms} ml=".5rem">
                  <FormHelperText error>{t(errors.flags?.acceptTerms?.message!)}</FormHelperText>
                </Box>
              </FormControl>
            </Box>
            <Box pl=".5rem" mb="1.5rem">
              <Typography variant="caption">{t('fields_with_asterisks')}</Typography>
            </Box>
            <Box hidden={!globalError} ml=".5rem" mb="1rem">
              <Typography color="error" variant="body1">
                {globalError}
              </Typography>
            </Box>
            <Box textAlign="center">
              <AppButton color="secondary" loading={loading} type="submit" variant="contained" disabled={!watch()?.flags?.acceptTerms}>
                {t('auth_register_submit_label', 'Register and Continue')}
              </AppButton>
            </Box>
          </form>
            <Box mt="1.5rem" pl=".5rem">
              <Typography variant="caption" component="p">
                {t('newsletter_clarification')}
              </Typography>
            </Box>
          </Paper>
        </div>
      </Grid>
      <Grid item xs={12} lg={6} className={classes.loginBox} style={{ maxWidth: '38rem' }}>
        <div className={classes.loginSection}>
          <Paper className={cardStyles.card} elevation={0}>
            <Typography variant="h3" component="h2">
              {t('auth_register_login_heading', 'Do you already have an account?')}
            </Typography>
            <Box mb="1rem" />
            <Box textAlign="center">
              <Button color="secondary" component={Link} to={`${getPage('auth')}${getPage('login')}`} variant="contained">
                {t('Sign In')}
              </Button>
            </Box>
          </Paper>
        </div>
      </Grid>
    </Grid>
  );
}
